import { useMemo } from 'react';

import { BoardsFiltersSortBy, type BoardsListOptions } from '@air/api/types';

import { useFilterParams } from '~/hooks/filters/useFilterParams';
import { useSearchParams } from '~/hooks/search/useSearchParams';
import { useCurrentBoardIdSelector } from '~/hooks/useCurrentBoardIdSelector';
import { useURLLibraryIdSelector } from '~/hooks/useURLLibraryIdSelector';

export type UseBoardSearchParamsProps = {
  includeCoverImage?: boolean;
  includeDirectDescendantCount?: boolean;
  includeDirectDescendantBoardCount?: boolean;
};

export type UseBoardSearchParamsReturn = Pick<
  BoardsListOptions,
  | 'search'
  | 'ancestorCutoff'
  | 'numThumbnails'
  | 'view'
  | 'sortBy'
  | 'includeAncestors'
  | 'includeDirectDescendantBoardCount'
  | 'includeDirectDescendantCount'
  | 'includeCoverImage'
  | 'libraryIds'
  | 'libraryBoards'
>;

export const useBoardSearchParams = ({
  includeCoverImage,
  includeDirectDescendantCount,
  includeDirectDescendantBoardCount,
}: UseBoardSearchParamsProps = {}): UseBoardSearchParamsReturn => {
  // Canvas-aware: the `view`/`ancestorCutoff` params must scope to the drilled board inside the
  // editor's edit canvas (where there is no `/b/…` URL), and to the URL board id everywhere else —
  // byte-identical on the public/private board pages. See useCurrentBoardIdSelector.
  const boardId = useCurrentBoardIdSelector();
  const libraryId = useURLLibraryIdSelector();

  const { onlyInLibrariesParam } = useFilterParams();

  const { search } = useSearchParams();

  const boardsType = useMemo(() => {
    if (libraryId) {
      return 'YES';
    } else if (onlyInLibrariesParam?.is === false) {
      return 'NO';
    } else {
      return 'ALL';
    }
  }, [libraryId, onlyInLibrariesParam?.is]);

  const boardView = useMemo<BoardsListOptions['view']>(() => {
    if (boardId) {
      return boardId;
    } else if (search) {
      return 'all';
    } else {
      return 'root';
    }
  }, [boardId, search]);

  return {
    search,
    ancestorCutoff: boardId,
    numThumbnails: 1,
    sortBy: BoardsFiltersSortBy.custom,
    view: boardView,
    includeAncestors: true,
    includeDirectDescendantCount,
    includeDirectDescendantBoardCount,
    includeCoverImage,
    libraryIds: libraryId ? [libraryId] : undefined,
    libraryBoards: boardsType,
  };
};
